// What the GLOBAL Projects tab is allowed to show (D1-c §3.1).
//
// The design handoff §5.2 puts a Support card at release level on this tab.
// **Isaac has overridden that.** Support is a service desk shared by everyone;
// on the company view it is noise that pushes the release and the focus projects
// down the page, and the Tickets tab already owns the desk in full. Support
// belongs on a PERSON's tab, and only when that person actually has tickets.
//
// This lives in its own pure module purely so the rule is testable and cannot
// silently drift back the next time somebody re-reads the handoff.

import type { BrainProject } from "../lib/brain-api";

/** The one global service-desk project id. */
export const SUPPORT_PROJECT_ID = "support";

/** Ids that must never render as a card on the global Projects tab. */
export const GLOBAL_TAB_EXCLUDED = new Set([SUPPORT_PROJECT_ID]);

/** Is this the support desk (by id or by an explicit support alias)? */
export function isSupportProject(project: { projectId?: string | null }): boolean {
  const id = (project.projectId ?? "").trim().toLowerCase();
  return GLOBAL_TAB_EXCLUDED.has(id);
}

/**
 * The focus projects the global tab shows: the starred ones, minus Support.
 * `hidden` is returned so the caller can say so out loud rather than leaving a
 * starred project mysteriously missing.
 */
export function focusProjectsForGlobalTab<T extends { projectId: string; isFocus?: boolean }>(
  projects: T[],
): { shown: T[]; hidden: T[] } {
  const focus = projects.filter((p) => p.isFocus);
  return {
    shown: focus.filter((p) => !isSupportProject(p)),
    hidden: focus.filter((p) => isSupportProject(p)),
  };
}

/** Every project the "All projects" table may list — Support excluded here too. */
export function allProjectsForGlobalTab(projects: BrainProject[]): BrainProject[] {
  return projects.filter((p) => !isSupportProject(p));
}
